import {
  FirmwareComparison,
  compareFirmwareVersions,
  compareInverterFWModuleVersions,
  BYPASS_MIN_VERSION,
  ALLOW_SAME_VERSION,
} from 'ngx-modbus';
import { Channel } from 'ngx-core-services';

import {
  VERSION_SERIAL_NUMBERS,
  ModuleStatus,
  ModuleVersions,
  OnlineStatus,
  CommunicationChannelType,
  ModuleStatusOptions,
} from './inverter-status.constants';

export function extractLoggerVersion(loggerFwList: LoggerFwListResponse | null): string | undefined {
  if (!loggerFwList?.communicationFwDetails?.paramCategoryList) {
    return undefined;
  }

  return loggerFwList.communicationFwDetails.paramCategoryList
    ?.find((category) => category.serialNumber === VERSION_SERIAL_NUMBERS.VERSION_INFO_CATEGORY)
    ?.fieldList
    ?.find((field) => field.serialNumber === VERSION_SERIAL_NUMBERS.MODULE_VERSION_FIELD)
    ?.value
    ?.trim();
}

export function extractLoggerSn(loggerFwList: LoggerFwListResponse | null): string | undefined {
  return loggerFwList?.communicationFwDetails?.parDeviceSn;
}

export function extractDeviceSn(loggerFwList: LoggerFwListResponse | null): string | undefined {
  return loggerFwList?.communicationFwDetails?.deviceSn;
}

export function extractModuleVersionsFromSolarman(inverterFwList: InverterFwListResponse | null): ModuleVersions {
  if (!inverterFwList?.inverterFwDetails?.paramCategoryList) {
    return {};
  }

  const versionFields = inverterFwList.inverterFwDetails.paramCategoryList
    ?.find((category) => category.serialNumber === VERSION_SERIAL_NUMBERS.VERSION_INFO_CATEGORY)
    ?.fieldList;

  if (!versionFields) {
    return {};
  }

  return {
    arm: versionFields.find((f) => f.serialNumber === VERSION_SERIAL_NUMBERS.ARM_MODULE)?.value?.toUpperCase(),
    dspm: versionFields.find((f) => f.serialNumber === VERSION_SERIAL_NUMBERS.DSPM_MODULE)?.value?.toUpperCase(),
    dsps: versionFields.find((f) => f.serialNumber === VERSION_SERIAL_NUMBERS.DSPS_MODULE)?.value?.toUpperCase(),
  };
}

export function extractModuleVersionsFromZCS(zcsDeviceData: ZCSDeviceData | null | undefined): ModuleVersions {
  if (!zcsDeviceData) {
    return {};
  }

  return {
    arm: zcsDeviceData.arm?.toUpperCase(),
    dspm: zcsDeviceData.dspm?.toUpperCase(),
    dsps: zcsDeviceData.dsps?.toUpperCase(),
  };
}

export function calculateModuleStatus(
  currentVersion: string | undefined,
  latestVersion: string | undefined,
  options: ModuleStatusOptions = {}
): ModuleStatus {
  const {
    bypassMinVersion = BYPASS_MIN_VERSION,
    isLogger = false,
  } = options;

  if (!currentVersion) {
    return 'not_applicable';
  }

  if (!latestVersion) {
    return 'updated';
  }

  if (bypassMinVersion) {
    return 'needs_update';
  }

  const comparison = isLogger
    ? compareFirmwareVersions(currentVersion.toUpperCase(), latestVersion.toUpperCase())
    : compareInverterFWModuleVersions(currentVersion.toUpperCase(), latestVersion.toUpperCase());

  if (comparison === FirmwareComparison.LESS_THAN) {
    return 'needs_update';
  }

  if (comparison === FirmwareComparison.EQUAL) {
    return 'updated';
  }

  // GREATER_THAN or NOT_COMPARABLE
  return 'unknown';
}

export function isUpgradeAvailable(
  currentVersion: string | undefined,
  latestVersion: string | undefined,
  options: ModuleStatusOptions = {}
): boolean {
  const {
    bypassMinVersion = BYPASS_MIN_VERSION,
    allowSameVersion = ALLOW_SAME_VERSION,
    isLogger = false,
  } = options;

  if (!currentVersion || !latestVersion) {
    return false;
  }

  if (bypassMinVersion) {
    return true;
  }

  const comparison = isLogger
    ? compareFirmwareVersions(currentVersion.toUpperCase(), latestVersion.toUpperCase())
    : compareInverterFWModuleVersions(currentVersion.toUpperCase(), latestVersion.toUpperCase());

  // Upgrade available when current version is less than latest
  if (comparison === FirmwareComparison.LESS_THAN) {
    return true;
  }

  // For inverter modules: allow upgrade when versions are equal (re-flash)
  if (!isLogger && allowSameVersion && comparison === FirmwareComparison.EQUAL) {
    return true;
  }

  // Allow upgrade when current version is different from expected
  // (GREATER_THAN or NOT_COMPARABLE) - this handles version mismatch scenarios
  if (comparison === FirmwareComparison.GREATER_THAN || comparison === FirmwareComparison.NOT_COMPARABLE) {
    return true;
  }

  return false;
}

export function parseOnlineStatus(channelData: CommunicationChannelResponse | null | undefined): OnlineStatus {
  if (!channelData) {
    return { isOnline: null, communicationChannel: null };
  }

  const loggerStatus = channelData.LoggerStatus;
  const isOnline = loggerStatus === 'connected' ? true :
                   loggerStatus === 'disconnected' ? false : null;

  let communicationChannel: CommunicationChannelType = null;

  if (channelData.CommunicationChannel === Channel.Azzurro_MQTT) {
    communicationChannel = 'MQTT';
  } else if (channelData.CommunicationChannel === Channel.Azzurro_Ethernet) {
    communicationChannel = 'Ethernet';
  }

  return { isOnline, communicationChannel };
}

export function canConfigureDevice(
  isOnline: boolean | null,
  loggerStatus: ModuleStatus,
  isUpgrading: boolean
): boolean {
  if (isOnline !== true) {
    return false;
  }

  if (loggerStatus === 'needs_update') {
    return false;
  }

  if (isUpgrading) {
    return false;
  }

  return true;
}

export function canUpgradeDevice(isOnline: boolean | null): boolean {
  return isOnline === true;
}

export interface LoggerFwListResponse {
  communicationFwDetails: {
    parDeviceSn?: string;
    deviceSn?: string;
    paramCategoryList?: Array<{
      serialNumber: number;
      fieldList?: Array<{
        serialNumber: number;
        value?: string;
      }>;
    }>;
  };
  isLoggerOnline?: boolean;
}

export interface InverterFwListResponse {
  inverterFwDetails?: {
    paramCategoryList?: Array<{
      serialNumber: number;
      fieldList?: Array<{
        serialNumber: number;
        value?: string;
      }>;
    }>;
  };
}

export interface ZCSDeviceData {
  sn?: string;
  arm?: string;
  dspm?: string;
  dsps?: string;
  fota_status?: string;
}

export interface CommunicationChannelResponse {
  LoggerStatus?: string;
  CommunicationChannel?: string;
}
